'use client';

import type { DatePickerProps } from '@mui/x-date-pickers-pro';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { endOfDay, startOfDay } from 'date-fns';
import { forwardRef, useEffect, useRef, useState } from 'react';

import { useTimezoneUtilsRef } from 'Utils/date/date';

type Props = {
  value: Date | null;
  onChange: (value: Date | null) => void;
  label?: React.ReactNode;
  className?: string | undefined;
  isEnd?: boolean;
  useLocalTimezone?: boolean;
} & DatePickerProps;

export const Or2DatePicker = forwardRef<HTMLInputElement, Props>(
  (
    {
      value,
      onChange,
      isEnd = false,
      useLocalTimezone = false,
      slotProps = {},
      label,
      className,
      ...other
    }: Props,
    ref,
  ) => {
    const timezoneUtilsRef = useTimezoneUtilsRef();
    // SelectedDatetime is always in local time - what we pass out via value and
    // on change may not be
    const [selectedDatetime, setSelectedDatetime] = useState<null | Date>(
      useLocalTimezone ? value : timezoneUtilsRef.current.asLocalTime(value),
    );

    // Use a ref here rather than state because we don't want to render when this changes
    // We just want to reference it when other things (namely value) changes
    const uncommittedRef = useRef(false);

    // Keep the selectedDatetime
    useEffect(() => {
      if (!uncommittedRef.current) {
        setSelectedDatetime(
          useLocalTimezone
            ? value
            : timezoneUtilsRef.current.asLocalTime(value),
        );
      }
    }, [value, timezoneUtilsRef, useLocalTimezone]);

    const toNormalizedDatetime = (date: Date | null) => {
      if (!date) {
        return date;
      }

      let finalDate = isEnd ? endOfDay(date) : startOfDay(date);

      if (!useLocalTimezone) {
        // Put us into the correct timezone
        finalDate = timezoneUtilsRef.current.asUniversalTime(finalDate);
      }

      return finalDate;
    };

    const commitDate = (date = selectedDatetime) => {
      const normalizedDate = toNormalizedDatetime(date);
      if (
        (normalizedDate && normalizedDate.getTime()) !==
        (value && value.getTime())
      ) {
        onChange(normalizedDate);
      }

      uncommittedRef.current = false;
    };

    const handleBlur = () => {
      commitDate();
    };

    const handleChange = (date: Date | null) => {
      setSelectedDatetime(date);
      commitDate(date);
    };

    return (
      <DatePicker
        className={className}
        ref={ref}
        label={label}
        format="dd/MM/yyyy"
        value={selectedDatetime}
        onChange={handleChange}
        enableAccessibleFieldDOMStructure={false}
        slotProps={{
          textField: { onBlur: handleBlur, ...slotProps.textField },
          ...slotProps,
        }}
        {...other}
      />
    );
  },
);

Or2DatePicker.displayName = 'Or2DatePicker';
